import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError, fmtMXN, fmtDate } from "@/lib/api";
import { ColumnFilterRow, SortableHeaderRow, TableToolbar, useTableTools } from "@/components/TableFilters";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Wallet, AlertTriangle, Clock, PhoneCall, ExternalLink, History } from "lucide-react";
import { toast } from "sonner";

const CANALES = [["llamada", "Llamada"], ["correo", "Correo"], ["whatsapp", "WhatsApp"], ["visita", "Visita"], ["otro", "Otro"]];

const CAT = {
  vencida: { label: "Vencida", cls: "bg-red-100 text-red-700 border-red-300" },
  por_vencer: { label: "Por vencer", cls: "bg-amber-100 text-amber-700 border-amber-300" },
  vigente: { label: "Vigente", cls: "bg-slate-100 text-slate-600 border-slate-300" },
};

function diasLabel(d) {
  if (d < 0) return `Vencida hace ${Math.abs(d)} día${Math.abs(d) === 1 ? "" : "s"}`;
  if (d === 0) return "Vence hoy";
  return `Vence en ${d} día${d === 1 ? "" : "s"}`;
}

const COLUMNS = [
  { key: "folio", label: "Factura", accessor: (r) => r.folio_interno, placeholder: "Factura" },
  { key: "pedido", label: "Pedido", accessor: (r) => r.order_folio, placeholder: "Pedido" },
  { key: "cliente", label: "Cliente", accessor: (r) => r.cliente, placeholder: "Cliente" },
  { key: "monto", label: "Monto", accessor: (r) => r.monto, placeholder: "Monto" },
  { key: "vence", label: "Vence", accessor: (r) => fmtDate(r.fecha_vencimiento), sortAccessor: (r) => r.fecha_vencimiento, placeholder: "Vence" },
  { key: "categoria", label: "Estatus", type: "select", accessor: (r) => r.categoria, options: Object.entries(CAT).map(([value, v]) => ({ value, label: v.label })) },
  { key: "_a", type: "none" },
];

function Kpi({ icon: Icon, label, value, sub, tone }) {
  const tones = { red: "text-red-600 bg-red-50 border-red-200", amber: "text-amber-600 bg-amber-50 border-amber-200", blue: "text-interactive bg-blue-50 border-blue-200" };
  return (
    <Card className={`p-4 rounded-lg border ${tones[tone] || ""}`} data-testid={`kpi-${label}`}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-medium"><Icon className="w-4 h-4" /> {label}</div>
      <div className="text-2xl font-heading font-bold mt-1 text-foreground">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
    </Card>
  );
}

export default function Cobranza() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [segOpen, setSegOpen] = useState(false);
  const [current, setCurrent] = useState(null);
  const [nota, setNota] = useState("");
  const [canal, setCanal] = useState("llamada");

  const load = () => api.get("/billing/collections").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const rows = data?.invoices || [];
  const tools = useTableTools(rows, COLUMNS);

  const openSeg = (r) => { setCurrent(r); setNota(""); setCanal("llamada"); setSegOpen(true); };
  const submitSeg = async () => {
    if (!nota.trim()) return toast.error("Escribe la nota de seguimiento");
    try {
      await api.post(`/sales-invoices/${current.id}/followup`, { nota, canal });
      toast.success("Seguimiento registrado");
      setSegOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!data) return <div className="p-8 text-muted-foreground">Cargando cobranza...</div>;
  const k = data.kpis;

  return (
    <div className="space-y-6" data-testid="cobranza-page">
      <div>
        <h1 className="text-2xl font-heading font-bold tracking-tight flex items-center gap-2"><Wallet className="w-6 h-6" /> Panel de Cobranza</h1>
        <p className="text-sm text-muted-foreground mt-1">Facturas enviadas pendientes de pago, por vencer y vencidas. Registra el seguimiento con cada cliente.</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={Wallet} label="Por cobrar" value={fmtMXN(k.total_por_cobrar)} sub={`${k.n_total} factura${k.n_total === 1 ? "" : "s"} pendientes`} tone="blue" />
        <Kpi icon={AlertTriangle} label="Vencido" value={fmtMXN(k.total_vencido)} sub={`${k.n_vencidas} factura${k.n_vencidas === 1 ? "" : "s"} vencida${k.n_vencidas === 1 ? "" : "s"}`} tone="red" />
        <Kpi icon={Clock} label={`Por vencer (${data.soon_days}d)`} value={fmtMXN(k.total_por_vencer)} sub={`${k.n_por_vencer} próxima${k.n_por_vencer === 1 ? "" : "s"} a vencer`} tone="amber" />
        <Kpi icon={Wallet} label="Facturas" value={k.n_total} sub="en seguimiento de cobranza" tone="blue" />
      </div>

      {(k.n_vencidas > 0 || k.n_por_vencer > 0) && (
        <div className="space-y-2" data-testid="cobranza-alerts">
          {k.n_vencidas > 0 && (
            <div className="flex items-center gap-2 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="alert-vencidas">
              <AlertTriangle className="w-4 h-4 shrink-0" /> Tienes <b>{k.n_vencidas}</b> factura(s) <b>vencida(s)</b> por {fmtMXN(k.total_vencido)}. Da seguimiento hoy.
            </div>
          )}
          {k.n_por_vencer > 0 && (
            <div className="flex items-center gap-2 p-3 rounded-md bg-amber-50 border border-amber-200 text-amber-700 text-sm" data-testid="alert-porvencer">
              <Clock className="w-4 h-4 shrink-0" /> <b>{k.n_por_vencer}</b> factura(s) vencen en los próximos {data.soon_days} días ({fmtMXN(k.total_por_vencer)}).
            </div>
          )}
        </div>
      )}

      <Card className="p-5 rounded-lg">
        <TableToolbar tools={tools} columns={COLUMNS} filename="cobranza" />
        {rows.length === 0
          ? <div className="text-sm text-muted-foreground py-8 text-center">No hay facturas pendientes de cobro. ¡Todo al día! 🎉</div>
          : (
          <Table>
            <TableHeader>
              <SortableHeaderRow columns={COLUMNS} sort={tools.sort} toggleSort={tools.toggleSort} />
              {tools.showFilters && <ColumnFilterRow columns={COLUMNS} filters={tools.filters} setFilters={tools.setFilters} />}
            </TableHeader>
            <TableBody>
              {tools.view.map((r) => (
                <TableRow key={r.id} data-testid={`cobranza-row-${r.folio_interno}`} className={r.categoria === "vencida" ? "bg-red-50/40" : ""}>
                  <TableCell className="font-mono font-medium">{r.folio_interno}</TableCell>
                  <TableCell className="font-mono text-xs">{r.order_folio}</TableCell>
                  <TableCell>{r.cliente}</TableCell>
                  <TableCell className="font-medium">{fmtMXN(r.monto)}</TableCell>
                  <TableCell>
                    <div>{fmtDate(r.fecha_vencimiento)}</div>
                    <div className={`text-xs ${r.dias < 0 ? "text-red-600" : r.dias <= data.soon_days ? "text-amber-600" : "text-muted-foreground"}`}>{diasLabel(r.dias)}</div>
                  </TableCell>
                  <TableCell><Badge variant="outline" className={CAT[r.categoria]?.cls}>{CAT[r.categoria]?.label}</Badge></TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {r.last_followup && <span className="text-xs text-muted-foreground mr-2 inline-flex items-center gap-1"><History className="w-3 h-3" /> {fmtDate(r.last_followup)}</span>}
                    <Button size="sm" variant="outline" className="h-8 mr-1" onClick={() => openSeg(r)} data-testid={`followup-btn-${r.folio_interno}`}><PhoneCall className="w-3.5 h-3.5 mr-1" /> Seguimiento</Button>
                    {r.order_id && <Button size="sm" variant="ghost" className="h-8" onClick={() => nav(`/app/pedidos/${r.order_id}`)} data-testid={`view-order-${r.folio_interno}`}><ExternalLink className="w-3.5 h-3.5" /></Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={segOpen} onOpenChange={setSegOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Seguimiento de cobranza · <span className="font-mono">{current?.folio_interno}</span></DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="text-sm text-muted-foreground">{current?.cliente} · {fmtMXN(current?.monto || 0)} · {diasLabel(current?.dias ?? 0)}</div>
            <div>
              <Label>Canal</Label>
              <Select value={canal} onValueChange={setCanal}>
                <SelectTrigger data-testid="followup-canal"><SelectValue /></SelectTrigger>
                <SelectContent>{CANALES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Nota</Label><Textarea rows={3} value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Ej. Se contactó a contabilidad; prometen pago el viernes." data-testid="followup-nota" /></div>
            {(current?.seguimientos || []).length > 0 && (
              <div className="border rounded-md divide-y max-h-40 overflow-auto" data-testid="followup-history">
                <div className="px-3 py-1.5 text-xs uppercase tracking-wider text-muted-foreground bg-muted/40">Historial</div>
                {[...current.seguimientos].reverse().map((s) => (
                  <div key={s.id} className="px-3 py-2 text-sm">
                    <div className="text-xs text-muted-foreground">{fmtDate(s.fecha)} · {s.canal} · {s.por}</div>
                    <div>{s.nota}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter><Button onClick={submitSeg} data-testid="followup-submit"><PhoneCall className="w-4 h-4 mr-1" /> Registrar seguimiento</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
